/** @jsx jsx */
import { forwardRef, useState } from 'react';
import { css, jsx } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import type { PopupComponentProps } from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import { ColorPickerForm } from '@atlassian/jira-option-color-picker/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import type { ColorOptionData } from '@atlassian/jira-option-color-picker/src/types.tsx';
import type { OptionType } from '../../../../../../types/layout-items.tsx';
import messages from './messages.tsx';

const PopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<div {...props} ref={ref} css={popupContainerStyles} />
));

export const OptionColorPicker = ({
	canModifyOptions,
	fieldType,
	option,
	onSave,
}: {
	canModifyOptions: boolean;
	fieldType: string;
	option: OptionType;
	onSave: (data: ColorOptionData) => void;
}) => {
	const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false);
	const intl: IntlShape = useIntl();
	const [{ content: sidebarContent }] = useSidebar();

	if (!canModifyOptions || fieldType !== 'com.atlassian.jira.plugin.system.customfieldtypes:select')
		return null;
	if (
		sidebarContent ||
		!expVal(
			'thor_colourful_single_select_milestone2_experiment',
			'isColorfulSingleSelectEnabled',
			false,
		)
	)
		return null;
	return (
		<Popup
			isOpen={isColorPickerOpen}
			onClose={() => setIsColorPickerOpen(false)}
			placement="bottom-start"
			messageId="project-settings-apps-issue-type-page-product-features.common.ui.field-active.field-configuration.field-options.option.color-picker.popup"
			messageType="transactional"
			shouldRenderToParent
			popupComponent={PopupContainer}
			content={() => (
				<ColorPickerForm
					onCancel={() => setIsColorPickerOpen(!isColorPickerOpen)}
					onSubmit={(data) => {
						setIsColorPickerOpen(!isColorPickerOpen);
						onSave(data);
					}}
					initialData={{ name: option.value, color: option.colour ?? '' }}
					type="edit"
					projectType="software"
					colorPaletteVariant="new"
					colorCardVariant="outline"
					editLabel={false}
					showCancelButton={false}
				/>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					label={intl.formatMessage(messages.colorPickerNonFinal)}
					appearance="subtle"
					icon={EditIcon}
					onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
				/>
			)}
		/>
	);
};

const popupContainerStyles = css({
	zIndex: layers.layer(),
});
